import React, { useEffect, useMemo, useState } from 'react';
import { Button, ConfigProvider, Form, Input, Select, Upload } from "antd";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { InboxOutlined, LockOutlined } from "@ant-design/icons";
import useAxiosClient from "../axios-client.js";
import { useTeams } from "../hooks/useTeams.jsx";
import { useStateContext } from "../contexts/ContextProvider.jsx";

// Design tokens ported from the "Create User Redesign" mock, same shell
// palette as DefaultLayout, Users, Access (RoleSettings) and Login. This page
// sits inside DefaultLayout, but the mock's own dark surfaces are still set
// as CSS variables here (rather than inherited) so the nested ConfigProvider's
// portalled dropdowns can read the same hex values.
import { PRIMARY, SHELL_THEME as THEME, shellAntdTheme, STATUS } from "../shellTokens";
import { bdPhoneRule, normalizeBdPhone } from "../utils/validators.js";

const { Option } = Select;
const { Dragger } = Upload;
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

const FIELD_LABELS = {
  firstName: 'First name', lastName: 'Last name', phone: 'Phone', teamId: 'Team',
  email: 'Email', password: 'Password', cpassword: 'Confirm password',
  division: 'Division', district: 'District', upazila: 'Upazila / Thana', address: 'Street address',
};

// Purely derived from the typed value — length + character variety — never a
// fabricated "strength score" from the backend.
const passwordStrength = (pw) => {
  if (!pw) return { score: 0, label: '' };
  if (pw.length < 6) return { score: 1, label: 'Too short' };
  let score = 1;
  if (pw.length >= 8) score++;
  if (/[a-z]/.test(pw) && /[A-Z]/.test(pw) && /\d/.test(pw)) score++;
  if (score >= 3 && /[^A-Za-z0-9]/.test(pw)) score = 3;
  const labels = ['', 'Weak', 'Fair', 'Good'];
  return { score, label: labels[score] };
};

const filledCount = (values, fields) => fields.filter((f) => {
  const v = values[f];
  return v !== undefined && v !== null && String(v).trim() !== '';
}).length;

const UserForm = () => {

  const axiosClient = useAxiosClient();
  const navigate = useNavigate();
  const { state } = useLocation();
  const { teams, teamsLoading } = useTeams();
  const { permissions, darkMode } = useStateContext();
  const [form] = Form.useForm();

  const isEdit = !!state?.user;
  // Picking a starting role is part of creating a user, not part of
  // administering roles — CREATE_USER is enough to see the picker. The server
  // decides which roles that actually buys (each role carries `assignable`).
  const canManageRoles = (permissions || []).includes('ROLE_SETTINGS');
  const canPickRoles = canManageRoles || (permissions || []).includes('CREATE_USER');

  const [divisions, setDivisions] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [upazilas, setUpazilas] = useState([]);
  const [divisionLoading, setDivisionLoading] = useState(false);
  const [districtLoading, setDistrictLoading] = useState(false);
  const [upazilaLoading, setUpazilaLoading] = useState(false);

  const [uploaded, setUploaded] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [imageData, setImageData] = useState({});
  const [fileList, setFileList] = useState([]);

  const [roles, setRoles] = useState([]);
  const [rolesLoading, setRolesLoading] = useState(false);

  const [loading, setLoading] = useState(false);
  const [errorSummary, setErrorSummary] = useState([]);
  const [createdUser, setCreatedUser] = useState(null); // { name, email, roleNames }

  // Snapshot taken once — the "discard" baseline in edit mode and the seed
  // for antd's own `initialValues` (which also powers form.resetFields()).
  const initialValues = useMemo(() => {
    if (!isEdit) return {};
    const { user } = state;
    return {
      firstName: user.firstname,
      lastName: user.lastname,
      email: user.email,
      teamId: user.team_id,
      address: user.address?.address,
      phone: user.address?.phone,
      division: user.address?.division?.value ? Number(user.address.division.value) : undefined,
      district: user.address?.district?.value ? Number(user.address.district.value) : undefined,
      upazila: user.address?.upazila?.value ? Number(user.address.upazila.value) : undefined,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const watched = Form.useWatch([], form) || {};

  const getDivisions = async () => {
    setDivisionLoading(true);
    const res = await axiosClient.get(`/getDivisions`);
    setDivisionLoading(false);
    return res.data;
  };
  const getDistricts = async (id) => {
    setDistrictLoading(true);
    const res = await axiosClient.get(`/getDistrictsByDivision?divisionId=${id}`);
    setDistrictLoading(false);
    return res.data;
  };
  const getUpazilas = async (id) => {
    setUpazilaLoading(true);
    const res = await axiosClient.get(`/getUpazilasByDistrict?districtId=${id}`);
    setUpazilaLoading(false);
    return res.data;
  };

  // Re-derives the district/upazila option lists for whatever division and
  // district the user currently has — used on mount and again by "Discard".
  const loadEditCascade = async () => {
    if (!isEdit) return;
    const { user } = state;
    const divisionId = user.address?.division?.value ? Number(user.address.division.value) : null;
    const districtId = user.address?.district?.value ? Number(user.address.district.value) : null;
    setDistricts(divisionId ? await getDistricts(divisionId) : []);
    setUpazilas(districtId ? await getUpazilas(districtId) : []);
  };

  const seedImage = () => {
    if (!isEdit) return;
    const { user } = state;
    if (user.image) {
      setUploaded(true);
      setImageData(user.image);
      setFileList([{
        uid: user.image.id,
        name: 'Existing image',
        status: 'done',
        url: `${process.env.REACT_APP_API_BASE_URL}/files/upload/${user.image.id}`,
      }]);
    } else {
      setUploaded(false);
      setImageData({});
      setFileList([]);
    }
  };

  useEffect(() => {
    getDivisions().then(setDivisions);
    loadEditCascade();
    seedImage();
    if (!isEdit && canPickRoles) {
      setRolesLoading(true);
      axiosClient.get('/settings/getRoles')
        .then((res) => setRoles(res.data || []))
        .catch(() => { /* roles picker just stays empty */ })
        .finally(() => setRolesLoading(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onDivisionSelect = async (value) => {
    const data = await getDistricts(value);
    setDistricts(data);
    setUpazilas([]);
    form.setFieldsValue({ district: undefined, upazila: undefined });
  };

  const onDistrictSelect = async (value) => {
    const data = await getUpazilas(value);
    setUpazilas(data);
    form.setFieldsValue({ upazila: undefined });
  };

  const draggerProps = {
    name: 'userImage',
    accept: "image/*",
    listType: "picture",
    multiple: false,
    action: `${process.env.REACT_APP_API_BASE_URL}/files/upload`,
    maxCount: 1,
    fileList,
    disabled: uploaded || loading,
    style: { background: 'var(--surface2)', border: '1px dashed var(--borderStrong)', borderRadius: 12 },
    onChange(info) {
      const { status } = info.file;
      setFileList(info.fileList.filter((file) => file.size <= MAX_FILE_SIZE));
      if (status === 'uploading') setUploading(true);
      if (status === 'done') {
        setUploading(false);
        setUploaded(true);
        setImageData(info.file.response);
        toast.success(`${info.file.name} uploaded successfully.`);
      } else if (status === 'error') {
        toast.error(`${info.file.name} upload failed.`);
      }
    },
    beforeUpload(file) {
      if (file.size / 1024 / 1024 > 5) {
        toast.error('File size must be smaller than 5MB');
        return false;
      }
      return true;
    },
    onRemove() {
      setUploaded(false);
      setImageData({});
    },
  };

  const discardChanges = () => {
    form.resetFields();
    loadEditCascade();
    seedImage();
    setErrorSummary([]);
  };

  const resetForAnother = () => {
    form.resetFields();
    setDistricts([]);
    setUpazilas([]);
    setUploaded(false);
    setImageData({});
    setFileList([]);
    setErrorSummary([]);
    setCreatedUser(null);
  };

  const onFinishFailed = ({ errorFields }) => {
    setErrorSummary(errorFields.map((f) => ({ name: f.name[0], errors: f.errors })));
  };

  const onFinish = async (data) => {
    setErrorSummary([]);
    setLoading(true);
    const userData = { ...data, phone: normalizeBdPhone(data.phone), image: { ...imageData } };
    delete userData.roles;

    try {
      if (isEdit) {
        const response = await axiosClient.put(`/users/update/${state.user.id}`, userData);
        toast.success(response.data.message);
        navigate("/users");
        return;
      }

      // Roles go with the create call: assigning them afterwards meant hitting
      // the role editor's endpoint, which needs ROLE_SETTINGS — so anyone who
      // could only create users silently got a user with no role.
      const pickedRoles = data.roles || [];
      if (canPickRoles && pickedRoles.length) {
        userData.roles = pickedRoles;
      }

      const response = await axiosClient.post(`/users/store`, userData);
      const assignedRoles = response.data.roles || [];

      setCreatedUser({
        name: `${data.firstName} ${data.lastName}`.trim(),
        email: data.email,
        roleNames: assignedRoles,
      });
    } catch (error) {
      const body = (error.response && error.response.data) || {};
      const message = body.message || error.message || error.toString();
      // The server refuses roles it will not let this creator hand out, but the
      // user itself is already created — show the success panel with a warning
      // rather than leaving them on a form that will now fail on a duplicate
      // email.
      if (!isEdit && body.id) {
        toast.warning(message);
        setCreatedUser({
          name: `${data.firstName} ${data.lastName}`.trim(),
          email: data.email,
          roleNames: body.roles || [],
        });
        return;
      }
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const rootStyle = {
    ...Object.fromEntries(
      Object.entries(THEME[darkMode ? 'dark' : 'light']).map(([k, v]) => [`--${k}`, v])
    ),
    '--primary': PRIMARY,
  };

  if (createdUser) {
    return (
      <CreatedPanel
        rootStyle={rootStyle}
        user={createdUser}
        onAddAnother={resetForAnother}
        onBack={() => navigate('/users')}
      />
    );
  }

  const strength = passwordStrength(watched.password);
  const cpasswordMismatch = !!watched.cpassword && watched.password !== watched.cpassword;

  const identityFields = ['firstName', 'lastName', 'phone', 'teamId'];
  const signinFields = isEdit ? ['email'] : ['email', 'password', 'cpassword'];
  const addressFields = ['division', 'district', 'upazila', 'address'];

  const identityOk = filledCount(watched, identityFields) === identityFields.length && !!watched.phone;
  const passwordOk = !isEdit && !!watched.password && watched.password.length >= 6 && !!watched.cpassword && !cpasswordMismatch;
  const addressOk = filledCount(watched, addressFields) === addressFields.length;
  const selectedRoleObjs = (watched.roles || []).map((name) => roles.find((r) => r.name === name)).filter(Boolean);

  const changedFields = ['firstName', 'lastName', 'email', 'phone', 'teamId', 'division', 'district', 'upazila', 'address'];
  const changedCount = isEdit ? changedFields.filter((f) => (watched[f] ?? '') !== (initialValues[f] ?? '')).length : 0;

  const jumpToField = (name) => {
    form.scrollToField(name, { behavior: 'smooth', block: 'center' });
    setTimeout(() => form.getFieldInstance(name)?.focus?.(), 260);
  };

  return (
    <div className="et-userform" style={rootStyle}>
      <style>{`
        .et-userform{font-family:Outfit,system-ui,sans-serif;color:var(--text)}
        .et-userform .ant-form-item{margin-bottom:0}
        .et-userform .ant-form-item-label{padding-bottom:6px}
        .et-userform .ant-form-item-label>label{font-weight:600;font-size:12px;color:var(--muted)}
        .et-userform .ant-upload-list-item{background:var(--surface2);border:1px solid var(--border) !important;border-radius:10px;overflow:hidden}
        .et-userform__body{display:flex;gap:16px;align-items:flex-start;margin-top:16px}
        .et-userform__main{flex:1;min-width:0;display:flex;flex-direction:column;gap:16px}
        .et-userform__rail{flex:none;width:340px;display:flex;flex-direction:column;gap:16px}
        .et-userform__grid2{display:grid;grid-template-columns:1fr 1fr;gap:16px 18px}
        .et-userform__grid3{display:grid;grid-template-columns:1fr 1fr 1fr;gap:16px 18px}
        .et-userform__cardBody{display:flex;flex-direction:column;gap:16px}
        .et-userform__btnGhost{height:38px;padding:0 14px;border-radius:9px;border:1px solid var(--border);background:var(--surface);color:var(--muted);font:600 13px Outfit,sans-serif;cursor:pointer}
        .et-userform__btnGhost:hover{background:var(--hover);color:var(--text)}
        .et-userform__btnGhost:disabled{opacity:.45;cursor:default}
        .et-userform__errorItem:hover .et-userform__errorLabel{text-decoration:underline}
        @media (max-width:900px){
          .et-userform__body{flex-direction:column}
          .et-userform__rail{width:100%}
        }
        @media (max-width:640px){
          .et-userform__grid2,.et-userform__grid3{grid-template-columns:1fr}
        }
      `}</style>

      <ConfigProvider theme={shellAntdTheme(darkMode)}>
        <Form
          form={form}
          layout="vertical"
          initialValues={initialValues}
          disabled={loading}
          onFinish={onFinish}
          onFinishFailed={onFinishFailed}
        >
          {/* Header */}
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 24, flexWrap: 'wrap' }}>
            <div>
              <div style={{ font: '400 12px Outfit, sans-serif', color: 'var(--dim)' }}>
                <span style={{ cursor: 'pointer' }} onClick={() => navigate('/users')}>Users</span>{' '}
                <span style={{ color: 'var(--borderStrong)' }}>/</span> {isEdit ? 'Edit' : 'New'}
              </div>
              <div style={{ font: '700 22px Outfit, sans-serif', letterSpacing: '-.01em', marginTop: 1 }}>
                {isEdit ? `Edit ${state.user.firstname} ${state.user.lastname}` : 'Create user'}
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <button type="button" className="et-userform__btnGhost" onClick={() => navigate('/users')}>Cancel</button>
              <Button type="primary" htmlType="submit" loading={loading} disabled={uploading}>
                {isEdit ? 'Save changes' : 'Create user'}
              </Button>
            </div>
          </div>

          {errorSummary.length > 0 && (
            <div style={{ marginTop: 16, border: '1px solid rgba(248,113,113,.35)', background: 'rgba(248,113,113,.08)', borderRadius: 11, padding: '13px 14px', display: 'flex', flexDirection: 'column', gap: 9 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                <span style={{ color: STATUS.error, fontSize: 12 }}>!</span>
                <span style={{ font: '700 13px Outfit, sans-serif', color: STATUS.errorSoft }}>
                  {errorSummary.length} field{errorSummary.length === 1 ? '' : 's'} need attention
                </span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 5, font: '400 12.5px Outfit, sans-serif', color: 'var(--muted)' }}>
                {errorSummary.map((it) => (
                  <span key={it.name} className="et-userform__errorItem" onClick={() => jumpToField(it.name)} style={{ cursor: 'pointer' }}>
                    <span className="et-userform__errorLabel" style={{ color: 'var(--activeText)' }}>{FIELD_LABELS[it.name] || it.name}</span> — {it.errors[0]}
                  </span>
                ))}
              </div>
            </div>
          )}

          <div className="et-userform__body">
            <div className="et-userform__main">

              {/* Identity */}
              <Section index="1" title="Identity" filled={filledCount(watched, identityFields)} total={!isEdit ? identityFields.length : undefined}>
                <div className="et-userform__grid2">
                  <Form.Item name="firstName" label={<>First name <RequiredMark /></>} rules={[{ required: true, message: 'Please input your first name!' }]}>
                    <Input placeholder="First name" />
                  </Form.Item>
                  <Form.Item name="lastName" label={<>Last name <RequiredMark /></>} rules={[{ required: true, message: 'Please input your last name!' }]}>
                    <Input placeholder="Last name" />
                  </Form.Item>
                  <Form.Item
                    name="phone"
                    label={<>Phone <RequiredMark /></>}
                    rules={[{ required: true, message: 'Please input your phone number!' }, bdPhoneRule]}
                    extra={<span style={{ font: '400 11.5px Outfit, sans-serif', color: 'var(--dim)' }}>Used for delivery calls and login recovery.</span>}
                  >
                    <Input placeholder="01XXXXXXXXX" style={{ fontFamily: 'var(--mono, monospace)' }} />
                  </Form.Item>
                  <Form.Item name="teamId" label={<>Team <RequiredMark /></>} rules={[{ required: true, message: 'Please select a team!' }]}>
                    <Select placeholder="Select team" loading={teamsLoading}>
                      {teams.map((team) => <Option value={team.id} key={team.id}>{team.name}</Option>)}
                    </Select>
                  </Form.Item>
                </div>
              </Section>

              {/* Sign-in & access */}
              <Section index="2" title="Sign-in & access" filled={filledCount(watched, signinFields)} total={!isEdit ? signinFields.length : undefined}>
                {isEdit && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 11, padding: '11px 13px', borderRadius: 10, background: 'var(--surface2)', border: '1px solid var(--border)' }}>
                    <LockOutlined style={{ color: 'var(--muted)', fontSize: 12 }} />
                    <span style={{ font: '400 12.5px Outfit, sans-serif', color: 'var(--muted)' }}>Password is not shown or changed here.</span>
                  </div>
                )}
                <div className={isEdit ? undefined : 'et-userform__grid3'} style={isEdit ? { maxWidth: 380 } : undefined}>
                  <Form.Item
                    name="email"
                    label={<>Email <RequiredMark /></>}
                    rules={[{ required: true, message: 'Please input your Email!' }, { type: 'email', message: 'Enter a valid Email' }]}
                    extra={!isEdit && <span style={{ font: '400 11.5px Outfit, sans-serif', color: 'var(--dim)' }}>This is the username.</span>}
                  >
                    <Input placeholder="name@eleganttex.com" style={{ fontFamily: 'var(--mono, monospace)' }} />
                  </Form.Item>
                  {!isEdit && (
                    <>
                      <Form.Item
                        name="password"
                        label={<>Password <RequiredMark /></>}
                        rules={[{ required: true, message: 'Please input your Password!' }, { min: 6, message: 'Length minimum 6' }]}
                      >
                        <Input.Password placeholder="********" />
                      </Form.Item>
                      <Form.Item
                        name="cpassword"
                        label={<>Confirm password <RequiredMark /></>}
                        dependencies={['password']}
                        rules={[
                          { required: true, message: 'Please confirm your password!' },
                          ({ getFieldValue }) => ({
                            validator(_, value) {
                              if (!value || getFieldValue('password') === value) return Promise.resolve();
                              return Promise.reject(new Error('Passwords do not match.'));
                            },
                          }),
                        ]}
                      >
                        <Input.Password placeholder="********" />
                      </Form.Item>
                    </>
                  )}
                </div>
                {!isEdit && watched.password && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 7, maxWidth: 260 }}>
                    {[0, 1, 2].map((i) => (
                      <span key={i} style={{ flex: 1, height: 3, borderRadius: 2, background: i < strength.score ? (strength.score >= 3 ? STATUS.ok : strength.score === 2 ? STATUS.warn : STATUS.error) : 'var(--border)' }} />
                    ))}
                    <span style={{ font: '600 11px Outfit, sans-serif', color: strength.score >= 3 ? STATUS.ok : strength.score === 2 ? STATUS.warn : STATUS.errorSoft }}>{strength.label}</span>
                  </div>
                )}
                {!isEdit && canPickRoles && (
                  <Form.Item
                    name="roles"
                    label="Roles"
                    extra={<span style={{ font: '400 11.5px Outfit, sans-serif', color: 'var(--dim)' }}>A user with no role can sign in but sees nothing. Roles can be changed later in Access.</span>}
                  >
                    <Select mode="multiple" placeholder="Add a role…" loading={rolesLoading} optionLabelProp="label">
                      {roles.map((r) => (
                        <Option value={r.name} key={r.name} label={r.name} disabled={r.assignable === false}>
                          <span style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
                            <span>{r.name}</span>
                            <span style={{ color: 'var(--dim)', fontSize: 11 }}>
                              {r.assignable === false
                                ? 'grants access you don\u2019t have'
                                : `${r.permissionsCount} permission${r.permissionsCount === 1 ? '' : 's'}`}
                            </span>
                          </span>
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                )}
              </Section>

              {/* Address */}
              <Section index="3" title="Address" filled={filledCount(watched, addressFields)} total={!isEdit ? addressFields.length : undefined}>
                <div className="et-userform__grid3">
                  <Form.Item name="division" label={<>Division <RequiredMark /></>} rules={[{ required: true, message: 'Please select division!' }]}>
                    <Select loading={divisionLoading} onSelect={onDivisionSelect} placeholder="Select division">
                      {divisions.map((d) => <Option value={d.id} key={d.id}>{d.name}</Option>)}
                    </Select>
                  </Form.Item>
                  <Form.Item name="district" label={<>District <RequiredMark /></>} rules={[{ required: true, message: 'Please select district!' }]}>
                    <Select loading={districtLoading} onSelect={onDistrictSelect} placeholder="Select district">
                      {districts.map((d) => <Option value={d.id} key={d.id}>{d.name}</Option>)}
                    </Select>
                  </Form.Item>
                  <Form.Item
                    name="upazila"
                    label={<>Upazila / Thana <RequiredMark /></>}
                    rules={[{ required: true, message: 'Please select upazila/thana!' }]}
                    extra={<span style={{ font: '400 11.5px Outfit, sans-serif', color: 'var(--dim)' }}>Follows the district above.</span>}
                  >
                    <Select loading={upazilaLoading} placeholder="Select upazila">
                      {upazilas.map((d) => <Option value={d.id} key={d.id}>{d.name}</Option>)}
                    </Select>
                  </Form.Item>
                </div>
                <Form.Item name="address" label={<>Street address <RequiredMark /></>} rules={[{ required: true, message: 'Please input your address!' }]}>
                  <Input.TextArea rows={4} placeholder="House, road, area…" />
                </Form.Item>
              </Section>
            </div>

            <div className="et-userform__rail">
              {/* Photo */}
              <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, overflow: 'hidden' }}>
                <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ font: '700 14.5px Outfit, sans-serif' }}>Photo</div>
                  <span style={{ font: '500 11.5px Outfit, sans-serif', color: 'var(--dim)' }}>Optional</span>
                </div>
                <div style={{ padding: 18 }}>
                  <Dragger {...draggerProps}>
                    <p style={{ margin: '0 0 9px' }}>
                      <span style={{ width: 40, height: 40, borderRadius: 12, background: 'var(--surface)', color: 'var(--muted)', fontSize: 15, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                        <InboxOutlined />
                      </span>
                    </p>
                    <div style={{ font: '600 13px Outfit, sans-serif', color: 'var(--text)' }}>
                      Drop an image or <span style={{ color: 'var(--activeText)' }}>browse</span>
                    </div>
                    <div style={{ font: '400 11.5px Outfit, sans-serif', color: 'var(--dim)', marginTop: 4 }}>JPG or PNG, one file, up to 5&nbsp;MB</div>
                  </Dragger>
                </div>
              </div>

              {isEdit ? (
                <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, overflow: 'hidden' }}>
                  <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--border)', font: '700 14.5px Outfit, sans-serif' }}>Save changes</div>
                  <div style={{ padding: '16px 18px', font: '400 12.5px Outfit, sans-serif', color: 'var(--muted)' }}>
                    {changedCount > 0 ? `${changedCount} field${changedCount === 1 ? '' : 's'} changed` : 'No changes yet'}
                  </div>
                  <div style={{ padding: '14px 18px', borderTop: '1px solid var(--border)', background: 'var(--surface2)', display: 'flex', gap: 9 }}>
                    <button type="button" className="et-userform__btnGhost" style={{ flex: 1 }} disabled={!changedCount} onClick={discardChanges}>Discard</button>
                    <Button type="primary" htmlType="submit" loading={loading} disabled={!changedCount || uploading} style={{ flex: 1 }}>Save changes</Button>
                  </div>
                </div>
              ) : (
                <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, overflow: 'hidden' }}>
                  <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--border)', font: '700 14.5px Outfit, sans-serif' }}>Before you create</div>
                  <div style={{ padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 11 }}>
                    <ChecklistRow ok={identityOk} okText="Identity complete" badText="Identity incomplete" />
                    <ChecklistRow ok={passwordOk} okText="Sign-in details complete" badText={cpasswordMismatch ? 'Confirm password does not match' : 'Password required'} />
                    <ChecklistRow ok={addressOk} okText="Address complete" badText="Address incomplete" />
                    {selectedRoleObjs.map((r) => (
                      <div key={r.name} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <span style={{ color: 'var(--muted)', fontSize: 12 }}>◍</span>
                        <span style={{ font: '400 12.5px Outfit, sans-serif', color: 'var(--muted)' }}>
                          {r.name} role grants {r.permissionsCount} permission{r.permissionsCount === 1 ? '' : 's'}
                        </span>
                      </div>
                    ))}
                  </div>
                  <div style={{ padding: '14px 18px', borderTop: '1px solid var(--border)', background: 'var(--surface2)' }}>
                    <Button type="primary" htmlType="submit" block loading={loading} disabled={uploading} style={{ fontWeight: 700 }}>Create user</Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </Form>
      </ConfigProvider>
    </div>
  );
};

/* ---------------------------------------------------------------------- */

function RequiredMark() {
  return <span style={{ color: 'var(--primary)' }}>*</span>;
}

function Section({ index, title, filled, total, children }) {
  return (
    <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, overflow: 'hidden' }}>
      <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 11 }}>
        <span style={{ width: 22, height: 22, borderRadius: 7, background: 'var(--activeBg)', color: 'var(--activeText)', font: '700 11px var(--mono, monospace)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{index}</span>
        <div style={{ font: '700 14.5px Outfit, sans-serif' }}>{title}</div>
        {total != null && (
          <span style={{ marginLeft: 'auto', font: '500 11.5px var(--mono, monospace)', color: 'var(--dim)' }}>{filled} of {total} filled</span>
        )}
      </div>
      <div className="et-userform__cardBody" style={{ padding: 18 }}>
        {children}
      </div>
    </div>
  );
}

function ChecklistRow({ ok, okText, badText }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <span style={{ color: ok ? STATUS.ok : STATUS.error, fontSize: 12 }}>{ok ? '✓' : '!'}</span>
      <span style={{ font: '400 12.5px Outfit, sans-serif', color: ok ? 'var(--muted)' : STATUS.errorSoft }}>{ok ? okText : badText}</span>
    </div>
  );
}

function CreatedPanel({ rootStyle, user, onAddAnother, onBack }) {
  return (
    <div className="et-userform" style={rootStyle}>
      <style>{`.et-userform{font-family:Outfit,system-ui,sans-serif;color:var(--text)}`}</style>
      <div style={{ maxWidth: 420, margin: '48px auto', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, padding: '30px 26px 26px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, textAlign: 'center' }}>
        <span style={{ width: 44, height: 44, borderRadius: 22, background: 'rgba(52,211,153,.16)', color: STATUS.ok, fontSize: 18, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✓</span>
        <div style={{ font: '700 15px Outfit, sans-serif' }}>{user.name} created</div>
        <div style={{ font: '400 12.5px/1.6 Outfit, sans-serif', color: 'var(--muted)', maxWidth: 320 }}>
          {user.roleNames.length > 0
            ? `${user.roleNames.join(', ')} role${user.roleNames.length === 1 ? '' : 's'} applied. `
            : 'No role applied yet — assign one from Access when ready. '}
          Share the email (<span style={{ fontFamily: 'var(--mono, monospace)', color: 'var(--text)' }}>{user.email}</span>) and password with them directly — sign-in details aren't emailed automatically.
        </div>
        <div style={{ display: 'flex', gap: 9, marginTop: 6 }}>
          <button type="button" className="et-userform__btnGhost" onClick={onAddAnother}>Add another</button>
          <Button type="primary" onClick={onBack}>Back to users</Button>
        </div>
      </div>
    </div>
  );
}

export default UserForm;
